import React from 'react';
import { useNavigate, Outlet, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  AppBar,
  Box,
  CssBaseline,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Divider,
  Breadcrumbs,
  Link,
  Fade,
  useTheme,
  useMediaQuery,
  Avatar,
  CircularProgress,
  Menu,
  MenuItem,
  Tooltip,
  Chip,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Assessment as AssessmentIcon,
  ExitToApp as LogoutIcon,
  NavigateNext as NavigateNextIcon,
  Person as PersonIcon,
  Message as MessageIcon,
  Instagram as InstagramIcon,
  Google as GoogleIcon,
  Business as BusinessIcon,
  ChevronLeft as ChevronLeftIcon,
  AccountCircle as AccountCircleIcon,
  ArrowDropDown as ArrowDropDownIcon,
  Chat as ChatIcon,
  PlayArrow as PlayArrowIcon,
  Feedback as FeedbackIcon,
  TextSnippet as TextSnippetIcon,
  People as PeopleIcon,
  Store as StoreIcon,
  AdminPanelSettings as AdminPanelSettingsIcon,
  Loyalty as LoyaltyIcon,
  CloudSync as CloudSyncIcon,
  History as HistoryIcon,
} from '@mui/icons-material';
import { logout, getUserProfile } from '../../features/auth/authSlice';
import { AppDispatch, RootState } from '../../app/store';
import { fetchBrandInfo } from '../../features/brand/brandSlice';

const drawerWidth = 240;

interface BrandInfo {
  brand_id: string;
  brand_name: string;
  position_type: string;
}

const DashboardLayout: React.FC = () => {
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [open, setOpen] = React.useState(true);
  const [userMenuAnchorEl, setUserMenuAnchorEl] = React.useState<null | HTMLElement>(null);
  
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const { user, token, isLoading: authLoading } = useSelector((state: RootState) => state.auth);
  const { brandInfo, isLoading: brandLoading } = useSelector((state: RootState) => state.brand);
  
  React.useEffect(() => {
    if (token && user) {
      dispatch(fetchBrandInfo());
    }
  }, [dispatch, token, user]);

  React.useEffect(() => {
    setOpen(!isMobile);
  }, [isMobile]);

  const handleDrawerToggle = () => {
    if (isMobile) {
      setMobileOpen(!mobileOpen);
    } else {
      setOpen(!open);
    }
  };

  const handleLogout = () => {
    dispatch(logout())
      .unwrap()
      .then(() => {
        navigate('/login');
      })
      .catch(() => {
        navigate('/login');
      });
  };

  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setUserMenuAnchorEl(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchorEl(null);
  };

  const menuItems = [
    { text: 'Overview', icon: <DashboardIcon />, path: '/dashboard' },
    { text: 'Unified Feedback', icon: <MessageIcon />, path: '/dashboard/feedback' },
    { text: 'Google Reviews', icon: <GoogleIcon />, path: '/dashboard/google-reviews' },
    { text: 'Guest Comment Cards', icon: <BusinessIcon />, path: '/dashboard/guest-comment-cards' },
    { text: 'Instagram DM', icon: <ChatIcon />, path: '/dashboard/conversations' },
    { text: 'Template Management', icon: <TextSnippetIcon />, path: '/dashboard/templates' },
    { text: 'Analytics', icon: <AssessmentIcon />, path: '/dashboard/analytics' },
    // { text: 'TikTok Comments', icon: <PlayArrowIcon />, path: '/dashboard/tiktok-comments' },
  ];

  const adminMenuItems = [
    { text: 'User Management', icon: <PeopleIcon />, path: '/dashboard/user-management' },
    { text: 'Outlet Management', icon: <StoreIcon />, path: '/dashboard/outlet-management' },
    { text: 'Brand Management', icon: <LoyaltyIcon />, path: '/dashboard/brand-management' },
    { text: 'Crawl Management', icon: <CloudSyncIcon />, path: '/dashboard/crawl-management' },
    { text: 'User Activity', icon: <HistoryIcon />, path: '/dashboard/user-activity' },
  ];

  const isAdmin = user?.is_admin === true;

  const getBreadcrumbs = () => {
    const pathSegments = location.pathname.split('/').filter(Boolean);
    return (
      <Breadcrumbs 
        separator={<NavigateNextIcon fontSize="small" />}
        aria-label="breadcrumb"
        sx={{ mb: 3, mt: 1 }}
      >
        <Link
          component="button"
          variant="body1"
          onClick={() => navigate('/dashboard')}
          sx={{ color: 'text.primary', textDecoration: 'none' }}
        >
          Dashboard
        </Link>
        {pathSegments.length > 1 && (
          <Typography color="text.primary" sx={{ textTransform: 'capitalize' }}>
            {pathSegments[1]}
          </Typography>
        )}
      </Breadcrumbs>
    );
  };

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Toolbar sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 2,
        bgcolor: 'primary.main',
        color: 'primary.contrastText'
      }}>
        <Avatar sx={{ bgcolor: 'primary.light' }}>CF</Avatar>
        <Typography variant="h6" noWrap component="div">
          Boga CFM system
        </Typography>
      </Toolbar>
      <Divider />
      <List sx={{ flexGrow: 1 }}>
        {menuItems.map((item) => (
          <ListItem
            button
            key={item.text}
            onClick={() => {
              navigate(item.path);
              if (isMobile) setMobileOpen(false);
            }}
            selected={location.pathname === item.path}
            sx={{
              borderRadius: 1,
              mx: 1,
              my: 0.5,
              '&.Mui-selected': {
                bgcolor: 'primary.light',
                '&:hover': {
                  bgcolor: 'primary.light',
                },
              },
            }}
          >
            <ListItemIcon sx={{ 
              color: location.pathname === item.path ? 'primary.main' : 'inherit'
            }}>
              {item.icon}
            </ListItemIcon>
            <ListItemText 
              primary={item.text}
              sx={{
                '& .MuiTypography-root': {
                  fontWeight: location.pathname === item.path ? 600 : 400,
                },
              }}
            />
          </ListItem>
        ))}
      </List>
      {isAdmin && (
        <>
          <Divider />
          <Box sx={{ px: 2, pt: 1.5, pb: 0.5 }}>
            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Admin
            </Typography>
          </Box>
          <List>
            {adminMenuItems.map((item) => (
              <ListItem
                button
                key={item.text}
                onClick={() => {
                  navigate(item.path);
                  if (isMobile) setMobileOpen(false);
                }}
                selected={location.pathname === item.path}
                sx={{
                  borderRadius: 1,
                  mx: 1,
                  my: 0.5,
                  '&.Mui-selected': {
                    bgcolor: 'primary.light',
                    '&:hover': {
                      bgcolor: 'primary.light',
                    },
                  },
                }}
              >
                <ListItemIcon sx={{
                  color: location.pathname === item.path ? 'primary.main' : 'inherit'
                }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.text}
                  sx={{
                    '& .MuiTypography-root': {
                      fontWeight: location.pathname === item.path ? 600 : 400,
                    },
                  }}
                />
              </ListItem>
            ))}
          </List>
        </>
      )}
      <Divider />
      <List>
        <ListItem
          button
          onClick={() => {
            navigate('/dashboard/developer-feedback');
            if (isMobile) setMobileOpen(false);
          }}
          selected={location.pathname === '/dashboard/developer-feedback'}
          sx={{
            borderRadius: 1,
            mx: 1,
            my: 0.5,
            '&.Mui-selected': {
              bgcolor: 'primary.light',
              '&:hover': {
                bgcolor: 'primary.light',
              },
            },
          }}
        >
          <ListItemIcon sx={{ 
            color: location.pathname === '/dashboard/developer-feedback' ? 'primary.main' : 'inherit'
          }}>
            <FeedbackIcon />
          </ListItemIcon>
          <ListItemText 
            primary="Developer Feedback"
            sx={{
              '& .MuiTypography-root': {
                fontWeight: location.pathname === '/dashboard/developer-feedback' ? 600 : 400,
              },
            }}
          />
        </ListItem>
      </List>
      <Divider />
      {authLoading || brandLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
          <CircularProgress size={24} />
        </Box>
      ) : (
        user && brandInfo && (
          <Box sx={{ p: 2, bgcolor: 'background.paper' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <PersonIcon color="primary" />
              <Typography variant="subtitle2" color="primary">User Information</Typography>
            </Box>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              Email: {user?.email || 'Not available'}
            </Typography>
            <Typography variant="subtitle2" color="primary" sx={{ mt: 1, mb: 0.5 }}>
              Brand Access:
            </Typography>
            {brandInfo.map((brand, index) => (
              <Box key={index} sx={{ mb: 1, pl: 1 }}>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  • {brand.brand_name} ({brand.position_type})
                </Typography>
              </Box>
            ))}
            <Divider />
          </Box>
        )
      )}
      <List>
        <ListItem 
          button 
          onClick={handleLogout}
          sx={{
            borderRadius: 1,
            mx: 1,
            my: 0.5,
            '&:hover': {
              bgcolor: 'error.light',
              '& .MuiListItemIcon-root': {
                color: 'error.main',
              },
            },
          }}
        >
          <ListItemIcon>
            <LogoutIcon />
          </ListItemIcon>
          <ListItemText primary="Logout" />
        </ListItem>
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        elevation={1}
        sx={{
          width: { sm: open ? `calc(100% - ${drawerWidth}px)` : '100%' },
          ml: { sm: open ? `${drawerWidth}px` : 0 },
          transition: theme.transitions.create(['margin', 'width'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
          bgcolor: 'background.paper',
          color: 'text.primary',
        }}
      >
        <Toolbar sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2 }}
            >
              {open && !isMobile ? <ChevronLeftIcon /> : <MenuIcon />}
            </IconButton>
            <Typography variant="h6" noWrap component="div">
              Customer Feedback Management
            </Typography>
          </Box>
          
          {user && (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {brandInfo && brandInfo.length > 0 && (
                <Chip
                  label={brandInfo[0].brand_name}
                  size="small"
                  color="primary"
                  sx={{ mr: 2, display: { xs: 'none', sm: 'flex' } }}
                />
              )}
              
              <Tooltip title="Account settings">
                <IconButton
                  onClick={handleUserMenuOpen}
                  size="small"
                  sx={{ 
                    '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' },
                    border: userMenuAnchorEl ? '1px solid' : 'none',
                    borderColor: 'primary.main'
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Avatar sx={{ width: 32, height: 32, mr: 1, bgcolor: 'primary.main' }}>
                      {user.email ? user.email.charAt(0).toUpperCase() : 'U'}
                    </Avatar>
                    <Typography 
                      variant="body2" 
                      sx={{ mr: 0.5, display: { xs: 'none', sm: 'block' } }}
                    >
                      {user.email}
                    </Typography>
                    <ArrowDropDownIcon />
                  </Box>
                </IconButton>
              </Tooltip>
              
              <Menu
                anchorEl={userMenuAnchorEl}
                open={Boolean(userMenuAnchorEl)}
                onClose={handleUserMenuClose}
                onClick={handleUserMenuClose}
                PaperProps={{
                  sx: {
                    mt: 1.5,
                    minWidth: 220,
                    boxShadow: '0px 8px 24px rgba(0,0,0,0.1)',
                  },
                }}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
              >
                <Box sx={{ px: 2, py: 1 }}>
                  <Typography variant="subtitle2" color="primary">
                    User Information
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    {user.email}
                  </Typography>
                  
                  <Divider sx={{ my: 1 }} />
                  
                  <Typography variant="subtitle2" color="primary">
                    Brand Access:
                  </Typography>
                  {brandInfo && brandInfo.map((brand, index) => (
                    <Typography key={index} variant="body2" sx={{ mt: 0.5 }}>
                      • {brand.brand_name} ({brand.position_type})
                    </Typography>
                  ))}
                </Box>
                
                <Divider />
                
                <MenuItem 
                  onClick={handleLogout}
                  sx={{ 
                    color: 'error.main',
                    '&:hover': { bgcolor: 'error.light' } 
                  }}
                >
                  <LogoutIcon fontSize="small" sx={{ mr: 1 }} />
                  Logout
                </MenuItem>
              </Menu>
            </Box>
          )}
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { sm: open ? drawerWidth : 0 }, flexShrink: { sm: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="persistent"
          open={open}
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              borderRight: '1px solid',
              borderColor: 'divider',
              transition: theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
              }),
            },
          }}
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${open ? drawerWidth : 0}px)` },
          minHeight: '100vh',
          bgcolor: 'background.default',
          transition: theme.transitions.create(['margin', 'width'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
          marginLeft: { sm: open ? `${0}px` : 0 },
        }}
      >
        <Toolbar />
        <Fade in timeout={500}>
          <Box>
            {getBreadcrumbs()}
            <Outlet />
          </Box>
        </Fade>
      </Box>
    </Box>
  );
};

export default DashboardLayout; 